import {useSelector} from 'react-redux'
import css from 'styled-jsx/css'
import Link from 'next/link'
import {headN} from '@cullylarson/f'
import {classnames} from '@@client/lib/classes'
import {slugify} from '@@client/lib/url'
import {useCart} from '@@client/cart/cart-hooks'
import {buildSearchUrl} from '@@client/parts/parts-helper'
import {getMakePhotoUrl} from '@@client/parts/make/make-photos'
import loadMakeListDistinctFeatured from '@@client/parts/make/loadMakeListDistinctFeatured'
import Layout from '@@client/app/Layout'
import Col100 from '@@client/components/Col100'
import SearchAndSlider, {Slide} from '@@client/home/SearchAndSlider'
import PartCard from '@@client/parts/PartCard'
import PartCardListHeading from '@@client/parts/PartCardListHeading'
import {PhotoGridItem} from '@@client/parts/PhotoGrid'
import {subHeadingStyle} from '@@client/parts/styles'
import loadNewArrivals from '@@client/parts/inventory/loadNewArrivals'
import Markdown from '@@client/components/Markdown'

const sidebarContainerStyle = css.resolve`
    margin-bottom: 40px;
`

const padLeftGutterStyle = css.resolve`
    @import '@css/variables.css';

    @media(--md) {
        padding-right: 0;
    }
`

export const IntroContainer = ({children}) => {
    return (
        <>
            <div className={classnames('outer', 'pad-left-gutter', 'pad-right-gutter', padLeftGutterStyle.className)}>
                <div className='container'>
                    {children}
                </div>
            </div>
            {padLeftGutterStyle.styles}
            <style jsx>
                {`
                    @import '@css/variables.css';

                    .outer {
                        background: $c--blue--lightest;
                    }

                    .container {
                        padding: 25px 25px 25px 0;

                        p {
                            color: $c--blue--darker;
                            font-size: $size--larger;
                        }
                    }

                    .details {
                        display: grid;
                        grid-gap: 40px;
                        grid-template-columns: 1fr 1fr;

                        @media(--lg) {
                            grid-template-columns: 2fr 1fr 1fr;
                        }
                    }
                `}
            </style>
        </>
    )
}

export const Section = ({children, heading, anchorName}) => {
    return (
        <>
            <a name={anchorName} />
            <div className={classnames('outer', 'pad-left-gutter', 'pad-right-gutter', padLeftGutterStyle.className)}>
                <div className='container'>
                    <div className='heading'>
                        <h2 className={subHeadingStyle.className}>{heading}</h2>
                    </div>

                    <div className='content'>
                        {children}
                    </div>
                </div>
            </div>
            {padLeftGutterStyle.styles}
            {subHeadingStyle.styles}
            <style jsx>
                {`
                    @import '@css/variables.css';

                    .outer {
                        @media(--lg) {
                            padding-right: 50px;
                        }
                    }

                    .container {
                        padding: 25px 0;
                        border-bottom: 1px solid $c--blue--dark-gray;

                        @media(--lg) {
                            display: grid;
                            grid-gap: 20px;
                            grid-template-columns: 1fr 4fr;
                        }

                        .outer:last-child & {
                            border: 0;
                        }
                    }

                    h2 {
                        font-size: $size--medium;
                        margin-bottom: 10px;
                    }
                `}
            </style>
        </>
    )
}

const OneBrand = ({makeName, partTypeSlug}) => {
    const photoUrl = getMakePhotoUrl(makeName)

    if(!photoUrl) return null

    const searchUrl = buildSearchUrl({partTypeSlug: partTypeSlug, makeName})

    return (
        <PhotoGridItem href={searchUrl} src={photoUrl} width='398' height='296' alt={`${makeName} logo`} label={makeName} />
    )
}

const Brands = ({partTypeSlug, heading, allBrandsUrl, allBrandsLinkText}) => {
    loadMakeListDistinctFeatured(1, 12)
    const makeStore = useSelector(state => state.parts.make)

    if(makeStore.listDistinctFeatured.doing) return null
    if(!makeStore.listDistinctFeatured.entities.length) return null

    return (
        <>
            <div className={sidebarContainerStyle.className}>
                <PartCardListHeading heading={heading} bg='blue' />

                <div className='brands'>
                    {makeStore.listDistinctFeatured.entities.map(x => (<OneBrand key={x.makeName} partTypeSlug={partTypeSlug} makeName={x.makeName} />))}
                </div>

                <p className='m--text-center'><Link href={allBrandsUrl}><a className='btn btn--yellow btn--slim--lr'>{allBrandsLinkText} &raquo;</a></Link></p>
            </div>

            {sidebarContainerStyle.styles}
            <style jsx>
                {`
                    .brands {
                        display: grid;
                        grid-template-columns: 1fr 1fr 1fr;
                        grid-gap: 15px;
                        margin-bottom: 30px;
                    }
                `}
            </style>
        </>
    )
}

const PartsList = ({parts}) => {
    if(!parts.length) return null

    return (
        <>
            <div className='parts'>
                {parts.map(x => <PartCard key={x.inventoryId} partInfo={x} />)}
            </div>

            <style jsx>
                {`
                    @import '@css/variables.css';

                    .parts {
                        display: grid;
                        grid-column-gap: 10px;
                        grid-template-columns: 1fr 1fr;

                        & > :global(*) {
                            margin-bottom: 10px;
                        }
                    }
                `}
            </style>
        </>
    )
}

const RecentlyViewed = () => {
    const cart = useCart()

    const parts = headN(2, cart.recentlyViewed)

    if(!parts.length) return null

    return (
        <>
            <div className={sidebarContainerStyle.className}>
                <PartCardListHeading heading='Recently Viewed' bg='blue' />

                <PartsList parts={parts} />
            </div>

            {sidebarContainerStyle.styles}
        </>
    )
}

const NewArrivals = ({parts, partTypeSlug, heading, shopAllLinkText}) => {
    if(!parts.length) return null

    const shopAllUrl = buildSearchUrl({partTypeSlug})

    return (
        <>
            <div className={sidebarContainerStyle.className}>
                <PartCardListHeading heading={heading} bg='blue' />

                <PartsList parts={parts} />

                <p className='m--text-center'><Link href={shopAllUrl}><a className='btn btn--yellow btn--slim--lr'>{shopAllLinkText} &raquo;</a></Link></p>
            </div>

            {sidebarContainerStyle.styles}
        </>
    )
}

export const ContentContainer = ({
    children,
    newArrivalsStore,
    partTypeSlug,
    newArrivalsHeading,
    shopAllLinkText,
    brandsHeading,
    allBrandsUrl,
    allBrandsLinkText,
}) => {
    return (
        <>
            <div className='container'>
                <div className='left-side'>
                    <div className='t-sm new-arrivals-mobile'>
                        <Col100>
                            <NewArrivals heading={newArrivalsHeading} shopAllLinkText={shopAllLinkText} parts={headN(2, newArrivalsStore.entities)} partTypeSlug={partTypeSlug} />
                        </Col100>
                    </div>

                    {children}
                </div>
                <div className='right-side a-md'>
                    <div className='pad-right-gutter'>
                        <NewArrivals heading={newArrivalsHeading} shopAllLinkText={shopAllLinkText} parts={headN(6, newArrivalsStore.entities)} partTypeSlug={partTypeSlug} />
                        <RecentlyViewed />
                        <Brands partTypeSlug={partTypeSlug} heading={brandsHeading} allBrandsUrl={allBrandsUrl} allBrandsLinkText={allBrandsLinkText} />
                    </div>
                </div>
            </div>
            <style jsx>
                {`
                    @import '@css/variables.css';

                    .container {
                        @media(--md) {
                            display: grid;
                            grid-gap: 20px;
                            grid-template-columns: 4fr 3fr;
                        }

                        @media(--lg) {
                            grid-template-columns: 5fr 3fr;
                        }
                    }

                    .right-side {
                        padding-top: 25px;
                    }

                    .new-arrivals-mobile {
                        margin-top: 20px;
                    }
                `}
            </style>
        </>
    )
}

export const SectionListItem = ({anchorName, children}) => {
    return (
        <>
            <li><a href={`#${anchorName}`}>{children}</a></li>
            <style jsx>
                {`
                    @import '@css/variables.css';

                    li {
                        margin: 0 0 10px 0;
                        padding: 0 0 10px 0;
                        border-bottom: 1px solid $c--blue--light-gray;

                        &:last-child { margin-bottom: 0; }

                        a {
                            text-decoration: none;
                            font-weight: $weight--bold;
                            text-transform: uppercase;
                            display: flex;
                            justify-content: space-between;
                            align-items: center;

                            &:after {
                                content: '';
                                display: block;
                                width: 8px;
                                height: 8px;
                                margin-right: 10px;
                                border-top: 2px solid $c--link--body;
                                border-right: 2px solid $c--link--body;
                                transform: rotate(45deg);
                                position: relative;
                            }

                            &:hover:after {
                                border-color: $c--link--body--hover
                            }
                        }
                    }
                `}
            </style>
        </>
    )
}

export const SectionList = ({children}) => {
    return (
        <>
            <ul className='section-list'>
                {children}
            </ul>
            <style jsx>
                {`
                    @import '@css/variables.css';

                    .section-list {
                        list-style: none;
                        margin: 0;
                        padding: 0;
                        grid-column: auto / span 2;

                        @media(--lg) {
                            grid-column: unset;
                        }
                    }
                `}
            </style>
        </>
    )
}

export function FeaturedPartPage({content}) {
    const slides = content.slides || []
    const sections = content.sections || []
    const newArrivalsStore = useSelector(state => state.parts.inventory.newArrivalsList)

    loadNewArrivals(content.partTypeSlug)

    return (
        <Layout title={content.title}>
            <SearchAndSlider partTypeSlug={content.partTypeSlug}>
                {slides.map(slide => (
                    <Slide
                        key={slide.id}
                        imageUrl={slide.backgroundImage.url}
                        alt={slide.backgroundImage.alternativeText}
                        heading={slide.title}
                        subContent={slide.subContent ? (<Markdown>{slide.subContent}</Markdown>) : null}
                    >
                        <Markdown>{slide.content}</Markdown>
                    </Slide>
                ))}
            </SearchAndSlider>
            <ContentContainer
                newArrivalsStore={newArrivalsStore}
                newArrivalsHeading={content.newArrivalsHeading}
                shopAllLinkText={content.newArrivalsLinkText}
                partTypeSlug={content.partTypeSlug}
                brandsHeading={content.brandsSectionHeading}
                allBrandsUrl={`/${content.slug}/brands`}
                allBrandsLinkText={content.brandsSectionLinkText}
            >
                <IntroContainer>
                    <div className='intro content'><Markdown>{content.introLeadContent}</Markdown></div>

                    <div className='details'>
                        <SectionList>
                            {sections.map(section => {
                                const slug = slugify(section.title)

                                return <SectionListItem key={section.id} anchorName={slug}>{section.title}</SectionListItem>
                            })}
                        </SectionList>

                        <div className='content'><Markdown>{content.introColumnOneContent}</Markdown></div>
                        <div className='content'><Markdown>{content.introColumnTwoContent}</Markdown></div>
                    </div>
                </IntroContainer>

                {sections.map(section => {
                    const slug = slugify(section.title)

                    return (
                        <Section key={section.id} heading={section.title} anchorName={slug}><div className='content'><Markdown>{section.content}</Markdown></div></Section>
                    )
                })}
            </ContentContainer>
            <style jsx>
                {`
                    @import '@css/variables.css';

                    .details {
                        display: grid;
                        grid-gap: 40px;
                        grid-template-columns: 1fr 1fr;

                        @media(--lg) {
                            grid-template-columns: 2fr 1fr 1fr;
                        }
                    }

                    .intro.content {
                        margin-block-end: 1em;
                    }

                    .content {
                        :global(h3) {
                            font-size: $size--regular;
                            font-weight: $weight--bold;
                            text-transform: uppercase;
                            text-decoration: none;
                            color: $c--blue--darker;

                            a:hover {
                                text-decoration: none;
                                font-weight: $weight--bold;
                            }
                        }

                        :global(ul) {
                            list-style: none;
                            padding-left: 0;

                            :global(li) {
                                display: flex;
                                font-style: italic;
                                margin-bottom: 10px;

                                &:before {
                                    content: '•';
                                    flex: 0 0 20px;
                                    position: relative;
                                    top: -1px;
                                    display: block;
                                    font-size: 15px;
                                }
                            }
                        }
                    }
                `}
            </style>
        </Layout>
    )
}
